/**
 * Generic follow-camera POI (point of interest) model.
 */

import { Coordinates } from '~/app';
import Turfmaster from '~/server/src/tmmodel';
import { transformRelX, transformRelY } from '~/utils';

import {
  filterVisibleHurdles,
  HurdleVisibilityInput,
  selectGlobalNextHurdle,
} from './hurdlePoi';
import { BoardHurdle } from './hurdleTypes';

export type CameraPoiConstraint = 'hard' | 'soft';

export interface CameraPoiContribution {
  source: string;
  constraint: CameraPoiConstraint;
  /** Controller-board coordinates included in this POI */
  points: Coordinates[];
}

export interface CameraPoiContext {
  round: number;
  activeHorseProgress: number[];
  hurdles: BoardHurdle[];
  hurdleVisibility: HurdleVisibilityInput;
}

export interface BoundingBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
}

export function bboxFromPoints(points: Coordinates[], minSize: number): BoundingBox | null {
  if (points.length === 0) return null;

  let minX = transformRelX(points[0].x);
  let minY = transformRelY(points[0].y);
  let maxX = minX;
  let maxY = minY;

  for (let i = 1; i < points.length; i++) {
    const x = transformRelX(points[i].x);
    const y = transformRelY(points[i].y);
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }

  return expandBBoxToMinSize({ minX, minY, maxX, maxY }, minSize);
}

export function unionBoundingBoxes(
  a: BoundingBox | null,
  b: BoundingBox | null,
  minSize: number,
): BoundingBox | null {
  if (!a) return b;
  if (!b) return a;

  return expandBBoxToMinSize(
    {
      minX: Math.min(a.minX, b.minX),
      minY: Math.min(a.minY, b.minY),
      maxX: Math.max(a.maxX, b.maxX),
      maxY: Math.max(a.maxY, b.maxY),
    },
    minSize,
  );
}

function expandBBoxToMinSize(
  raw: { minX: number; minY: number; maxX: number; maxY: number },
  minSize: number,
): BoundingBox {
  const width = raw.maxX - raw.minX;
  const height = raw.maxY - raw.minY;
  const effectiveWidth = Math.max(width, minSize);
  const effectiveHeight = Math.max(height, minSize);
  const widthDiff = effectiveWidth - width;
  const heightDiff = effectiveHeight - height;
  const minX = raw.minX - widthDiff / 2;
  const maxX = raw.maxX + widthDiff / 2;
  const minY = raw.minY - heightDiff / 2;
  const maxY = raw.maxY + heightDiff / 2;

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: effectiveWidth,
    height: effectiveHeight,
    centerX: minX + effectiveWidth / 2,
    centerY: minY + effectiveHeight / 2,
  };
}

/**
 * Resolve active POI contributions for follow-camera framing.
 */
export function resolveCameraPois(ctx: CameraPoiContext): CameraPoiContribution[] {
  const pois: CameraPoiContribution[] = [];

  if (!Turfmaster.hurdleRounds.includes(ctx.round)) {
    return pois;
  }

  const visible = filterVisibleHurdles(ctx.hurdles, ctx.hurdleVisibility);
  const next = selectGlobalNextHurdle(visible, ctx.activeHorseProgress);
  if (next && next.envelope.length > 0) {
    pois.push({
      source: 'hurdle-next',
      constraint: 'hard',
      points: next.envelope,
    });
  }

  return pois;
}

export function hasHardPoiConstraint(pois: CameraPoiContribution[]): boolean {
  return pois.some((p) => p.constraint === 'hard');
}
